import { useEffect, useSyncExternalStore } from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A module-level toast queue: any code — a mutation's `onError`, the login
 * form — can `toast()` without a provider in scope. `<Toaster />` at the root
 * subscribes and draws.
 */
type Kind = 'success' | 'error' | 'info';

export interface Toast { id: number; kind: Kind; title: string; message?: string }

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => { for (const l of listeners) l(); };

export function toast(kind: Kind, title: string, message?: string): void {
  toasts = [...toasts, { id: nextId++, kind, title, message }];
  emit();
}

export function dismiss(id: number): void {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

const ICON: Record<Kind, typeof Info> = { success: CheckCircle2, error: XCircle, info: Info };
const TONE: Record<Kind, string> = { success: 'text-ok', error: 'text-danger', info: 'text-accent' };

function Item({ item }: { item: Toast }) {
  useEffect(() => {
    const t = setTimeout(() => dismiss(item.id), item.kind === 'error' ? 9000 : 4500);
    return () => clearTimeout(t);
  }, [item.id, item.kind]);
  const Icon = ICON[item.kind];
  return (
    <div role="status" className="pointer-events-auto flex w-80 items-start gap-3 rounded-xl border border-line bg-surface p-3 shadow-card animate-pop">
      <Icon size={18} className={cn('mt-0.5 shrink-0', TONE[item.kind])} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{item.title}</p>
        {item.message && <p className="mt-0.5 break-words text-xs leading-5 text-muted">{item.message}</p>}
      </div>
      <button type="button" onClick={() => dismiss(item.id)} aria-label="Закрыть" className="text-faint hover:text-ink"><X size={14} /></button>
    </div>
  );
}

export function Toaster() {
  const items = useSyncExternalStore(subscribe, () => toasts, () => toasts);
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {items.map((t) => <Item key={t.id} item={t} />)}
    </div>
  );
}
